/**
 * a collection of useful string utilities
 * @module utils/string
 */

import html2markdown from 'html2markdown';
import markdownIt from 'markdown-it';
import markdownItAttrs from 'markdown-it-attrs';
import uuid from 'uuid/v4';

const ANCHOR_TAG_REGEX = /<a[^>]*>(?:.(?!<\/a>))*.<\/a>/g;
const HTML_TAG_REGEX = /<[^>]*>/g;

const markdown = markdownIt({ html: true }).use(markdownItAttrs);
export const renderMarkdown = markdown.render.bind(markdown);

/**
 * check whether a string contains only whitespace
 * @param {string} str - the input string
 * @returns {boolean} true if str is null or blank, otherwise false
 */
export const isBlank = function isStringBlank(str) {
  return !str || !str.trim().length;
};

/**
 * check whether a string is empty
 * @param {string} str - the input string
 * @returns {boolean} true if str is null or empty, otherwise false
 */
export const isEmpty = function isStringEmpty(str) {
  return !str || !str.length;
};

/**
 * creates a list of tags from a string
 * @param  {string} str - string
 * @returns {Array} list of tags
 */
export const createTags = function createTags(str) {
  return str.split('_').filter(val => val.trim().length);
};

/**
 * convert a string using a function without touching html anchor tags
 * @param {string} str - string to be converted
 * @param {Function} fn - function used for conversion
 * @returns {string} the converted string
 */
const convert = function convertSaveLinks(str, fn) {
  // nothing to do
  if (isBlank(str)) {
    return '';
  }

  // find and replace links with placeholder
  const placeholder = uuid();
  const links = str.match(ANCHOR_TAG_REGEX) || [];
  const placeholderStr = str.replace(ANCHOR_TAG_REGEX, placeholder);

  // convert and replace placeholder with links
  let retval = fn(placeholderStr).trim();
  links.forEach(link => {
    retval = retval.replace(placeholder, link);
  });

  return retval;
};

/**
 * convert html to markdown and preserve links
 * @param {string} html - the input html string
 * @returns {string} markdown string
 */
export const html2md = function convertHtmlToMarkdownSaveLinks(html = '') {
  return convert(html, html2markdown);
};

/**
 * convert markdown to html and preserve links
 * @param {string} md - the input markdown string
 * @returns {string} html string
 */
export const md2html = function convertMarkdownToHtmlSaveLinks(md = '') {
  return convert(md, renderMarkdown);
};

/**
 * replace indexValue placeholders from a string
 * @param {string} str - an input string that may contain indexValueN substrings
 * @param {...string} replaceIndicies - ordered values with which to replace substrings
 * @returns {string} a string with replaced values
 */
export const replaceIndexValues = function replaceLocationIndexValues(
  str,
  ...replaceIndicies
) {
  return !isBlank(str)
    ? replaceIndicies.reduce((acc, cur) => acc.replace(/indexValue[0-9]+/, cur), str)
    : '';
};

/**
 * remove html tags from the given content
 * @param {string} content - string content
 * @returns {string} text with html tags removed
 */
export const removeHTML = function removeHTMLTags(content) {
  if (typeof content !== 'string') {
    return content;
  }
  if (!content) {
    return undefined;
  }

  return content.replace(HTML_TAG_REGEX, '');
};

/**
 * remove href link from given content
 * @param {string} content - string content
 * @returns {string} link with html removed
 */
export const getLinkFromHTML = function getLinkFromHTML(content) {
  if (typeof content !== 'string') {
    return content;
  }
  if (!content) {
    return undefined;
  }
  return content.match(/href=".*?"/g).map(link => link.slice(6, -1)) || [];
};
